import React from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { useNavigate } from 'react-router-dom';

import { ApplicantStatusesEnum } from '@thinknet-gmbh/platform-common';
import { Loader, PageLayout } from '@thinknet-gmbh/platform-common-ui';

import { useGetEmployeeQuestionnaireById } from 'src/hooks/employee-questionnaire/get-employee-questionnaire-by-id.hook';
import { useAcceptEmployeeQuestionnaire } from 'src/hooks/employee-questionnaire/accept-employee-questionnaire.hook';
import { useUpdateApplicantById } from 'src/hooks/applicants/update-applicant-by-id.hook';

import { useRouteQuery } from '../../../../common/helpers';
import { removeNullAndUndefined } from '../../../../common/helpers/common.helpers';

import { ROUTES } from 'src/components/app/app-routes.constants';

import { QuestionnaireForm } from './components/questionnaire-form';

import { AcceptEmployeeWrapper } from './accept-questionnaire.styles';
import { IQuestionnaireFormInitialValue } from './accept-questionnaire.interfaces';

const AcceptQuestionnaire = () => {
  const navigate = useNavigate();

  const query = useRouteQuery();

  const _id: string = query.get('id') || '';

  const { updateApplicant } = useUpdateApplicantById({});

  const { employeeQuestionnaire, loading } = useGetEmployeeQuestionnaireById(_id as string);
  const acceptEmployeeQuestionnaire = useAcceptEmployeeQuestionnaire({
    onSuccess: () => navigate(ROUTES.employee),
  });

  const {
    employeePersonalInfo,
    emergencyContact,
    educationAndSkills,
    paymentAndInsurance,
    finishInformation,
  } = employeeQuestionnaire || {};

  const initialValues = {
    employeePersonalInfo: employeePersonalInfo
      ? {
          ...employeePersonalInfo,
          ...(employeePersonalInfo?.birthday && {
            birthday: moment(employeePersonalInfo.birthday),
            accessToPlatform: false,
          }),
        }
      : {},
    educationAndSkills: educationAndSkills || {},
    paymentAndInsurance: paymentAndInsurance
      ? {
          ...paymentAndInsurance,
          ...(paymentAndInsurance?.visaExpirationDate && {
            visaExpirationDate: moment(paymentAndInsurance.visaExpirationDate),
          }),
          ...(paymentAndInsurance?.workPermissionExpireDate && {
            workPermissionExpireDate: moment(paymentAndInsurance.workPermissionExpireDate),
          }),
          ...(paymentAndInsurance?.residencePermissionExpireDate && {
            residencePermissionExpireDate: moment(
              paymentAndInsurance.residencePermissionExpireDate
            ),
          }),
          ...(paymentAndInsurance?.certificateOfStudyExpireDate && {
            certificateOfStudyExpireDate: moment(paymentAndInsurance.certificateOfStudyExpireDate),
          }),
        }
      : {},
    emergencyContact: emergencyContact || {},
  };

  const onFinish = (values: IQuestionnaireFormInitialValue) => {
    const {
      employeePersonalInfo: employeePersonalInfoValues,
      emergencyContact: emergencyContactValues,
      educationAndSkills: educationAndSkillsValues,
      paymentAndInsurance: paymentAndInsuranceValues,
      permissionGroupId,
    } = values;

    const personalData =
      !isEmpty(employeePersonalInfoValues) &&
      employeePersonalInfoValues &&
      removeNullAndUndefined(employeePersonalInfoValues);
    const emergencyData =
      !isEmpty(emergencyContactValues) &&
      emergencyContactValues &&
      removeNullAndUndefined(emergencyContactValues);
    const educationData =
      !isEmpty(educationAndSkillsValues) &&
      educationAndSkillsValues &&
      removeNullAndUndefined(educationAndSkillsValues);
    const paymentData =
      !isEmpty(paymentAndInsuranceValues) &&
      paymentAndInsuranceValues &&
      removeNullAndUndefined({
        ...paymentAndInsuranceValues,
        healthInsuranceCardName: paymentAndInsurance?.healthInsuranceCardName,
      });

    const data = {
      permissionGroupId,
      ...(!isEmpty(personalData) && { employeePersonalInfo: personalData }),
      ...(!isEmpty(emergencyData) && { emergencyContact: emergencyData }),
      ...(!isEmpty(paymentData) && { paymentAndInsurance: paymentData }),
      ...(!isEmpty(educationData) && { educationAndSkills: educationData }),
      ...(!isEmpty(finishInformation) && { finishInformation }),
    };

    acceptEmployeeQuestionnaire({
      variables: {
        payload: {
          _id: employeeQuestionnaire._id,
          ...data,
        },
      },
      onCompleted: data => {
        const applicantId = data?.acceptEmployeeQuestionnaire?.applicantId;
        if (applicantId) {
          updateApplicant({
            variables: { applicantId, payload: { status: ApplicantStatusesEnum.HIRED } },
          });
        }
      },
    });
  };

  if (loading || !_id) {
    return <Loader />;
  }

  return (
    <PageLayout pageTitle="Add new employee">
      <AcceptEmployeeWrapper>
        <QuestionnaireForm loading={loading} initialValues={initialValues} onFinish={onFinish} />
      </AcceptEmployeeWrapper>
    </PageLayout>
  );
};

export default AcceptQuestionnaire;
